import type { DynamicBuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { AABB2 } from "geom/aabb2Types";
import type { Plane } from "geom/planeTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type RBush from "rbush";
import type {
  BuildingGraphFaceSideDirection,
  BuildingGraphFaceSideHandle,
} from "../buildingGraph/buildingGraphSideHandles";
import type { WallPlaneRuleHandle } from "../handles/docEntityHandleTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { WallPlaneAssemblyEntity } from "./wallPlaneAssemblyTypes";

export type WallPlaneOperationAABBRelativeSideValue = {
  readonly mode: "RELATIVE";
  readonly value: number;
};

export type WallPlaneOperationAABBDatumSideValue = {
  readonly mode: "DATUM";
  readonly datumId: ForeignEntityId<"DATUM">;
};

export type WallPlaneOperationAABBVerticalSideValue =
  | WallPlaneOperationAABBRelativeSideValue
  | WallPlaneOperationAABBDatumSideValue;

export type WallPlaneOperationAABBHorizontalDatumSideValue = {
  readonly mode: "HORIZONTAL_DATUM";
  readonly horizontalDatumId: ForeignEntityId<"DATUM">;
};

export type WallPlaneOperationAABBHorizontalSideValue =
  | WallPlaneOperationAABBRelativeSideValue
  | WallPlaneOperationAABBHorizontalDatumSideValue;

export type WallPlaneOperationAABB = {
  readonly start?: WallPlaneOperationAABBHorizontalSideValue;
  readonly end?: WallPlaneOperationAABBHorizontalSideValue;
  readonly bottom?: WallPlaneOperationAABBVerticalSideValue;
  readonly top?: WallPlaneOperationAABBVerticalSideValue;
};

/**
 * @TODO WALLS V2 - rename operationType to SET_WALL_CLADDING
 */
export type WallCladdingOperator<T extends BuildingGraphFaceSideDirection = BuildingGraphFaceSideDirection> = {
  readonly operationType: "SET_WALL_FINISH";
  readonly assemblySide: T;
  readonly assemblyId: ForeignEntityId<"WALL_PLANE_ASSEMBLY"> | null;
};

export type WallCoreOperator = {
  readonly operationType: "SET_WALL_CORE";
  readonly assemblyId: ForeignEntityId<"WALL_PLANE_ASSEMBLY"> | null;
};

export type WallOffsetOperator = {
  readonly operationType: "SET_WALL_OFFSET";
  readonly offset: number;
};

export type WallCutOperator = {
  readonly operationType: "SET_WALL_CUT";
};

export type WallPlaneOperator = WallCoreOperator | WallOffsetOperator | WallCladdingOperator | WallCutOperator;
export type ResolvedWallPlaneSide = "LEFT" | "RIGHT";

export enum WallPlaneRoofClippingSplitDirection {
  BELOW_ANY = "BELOW_ANY",
  BELOW_ALL = "BELOW_ALL",
  ABOVE_ANY = "ABOVE_ANY",
  ABOVE_ALL = "ABOVE_ALL",
}

export enum WallPlaneRoofClippingOperationMode {
  ALL = "ALL",
  NONE = "NONE",
  SELECTED = "SELECTED",
}

export type WallPlaneRoofSelectionAll = { readonly mode: WallPlaneRoofClippingOperationMode.ALL };
export type WallPlaneRoofSelectionNone = { readonly mode: WallPlaneRoofClippingOperationMode.NONE };
export type WallPlaneRoofSelectionSelected = {
  readonly mode: WallPlaneRoofClippingOperationMode.SELECTED;
  readonly roofIds: readonly ForeignEntityId<"ROOF">[];
};

export type WallPlaneRoofSelection =
  | WallPlaneRoofSelectionAll
  | WallPlaneRoofSelectionNone
  | WallPlaneRoofSelectionSelected;

export type WallPlaneOperationAABBBoundary = {
  readonly boundaryType: "AABB";
  readonly aabb: WallPlaneOperationAABB;
};

export type WallPlaneRoofClippingOperationBoundary = {
  readonly boundaryType: "ROOF_CLIPPING";
  readonly roofSelection: WallPlaneRoofSelection;
  readonly splitDirection: WallPlaneRoofClippingSplitDirection;
};

export type WallPlaneStairClippingOperationBoundary = {
  readonly boundaryType: "STAIR_CLIPPING";
};

export type WallPlaneOperationBoundary =
  | WallPlaneOperationAABBBoundary
  | WallPlaneRoofClippingOperationBoundary
  | WallPlaneStairClippingOperationBoundary;

export function isWallPlaneRoofClippingOperationBoundary(
  boundary: WallPlaneOperationBoundary,
): boundary is WallPlaneRoofClippingOperationBoundary {
  return boundary.boundaryType === "ROOF_CLIPPING";
}

export function isWallPlaneStairClippingOperationBoundary(
  boundary: WallPlaneOperationBoundary,
): boundary is WallPlaneStairClippingOperationBoundary {
  return boundary.boundaryType === "STAIR_CLIPPING";
}

export function getWallPlaneOperationAABB(
  boundary: WallPlaneOperationBoundary | undefined,
): WallPlaneOperationAABB | undefined {
  if (boundary?.boundaryType === "AABB") {
    return boundary.aabb;
  }
  return undefined;
}

export function isWallPlaneRoofClippingDomainOperation(operation: WallPlaneDomainOperation): boolean {
  return operation.operationBoundary?.boundaryType === "ROOF_CLIPPING";
}

export function isWallPlaneStairClippingDomainOperation(operation: WallPlaneDomainOperation): boolean {
  return operation.operationBoundary?.boundaryType === "STAIR_CLIPPING";
}

export interface WallPlaneDomainOperation<T extends WallPlaneOperator = WallPlaneOperator> {
  readonly rule: WallPlaneRuleHandle;
  readonly source: WallPlaneRuleHandle;

  readonly wallPlaneOperator: T;
  readonly wallPlaneFaceSideHandle: BuildingGraphFaceSideHandle;

  readonly operationBoundary?: WallPlaneOperationBoundary;
  /**
   * Resolved canonical wall-plane side precomputed from
   * `wallPlaneFaceSideHandle` during filter-match generation.
   */
  readonly resolvedFaceSide?: ResolvedWallPlaneSide;
}

export type WallPlaneCoreDirection = "LEFT_TO_RIGHT" | "RIGHT_TO_LEFT";

export type WallPlaneSubdomainInfo = {
  readonly wallThickness: number;
  readonly wallOffset: number;

  // TODO - flat array of layers in the assembly?.
  // index 0 should be left, index -1 should be right.

  readonly coreSource?: WallPlaneRuleHandle;
  readonly coreAssembly: WallPlaneAssemblyEntity | null;
  readonly coreThickness: number;
  readonly coreDirection: WallPlaneCoreDirection;

  readonly leftCoreToCladdingAirGap: number;
  readonly leftCladdingSource?: WallPlaneRuleHandle;
  readonly leftCladdingAssembly: WallPlaneAssemblyEntity | null;
  readonly leftCladdingThickness: number;

  readonly rightCoreToCladdingAirGap: number;
  readonly rightCladdingSource?: WallPlaneRuleHandle;
  readonly rightCladdingAssembly: WallPlaneAssemblyEntity | null;
  readonly rightCladdingThickness: number;
};

export type WallPlaneSubdomain = AABB2 & {
  readonly id: string;
  readonly operations: WallPlaneDomainOperation[];
  readonly roofClippingOperations: WallPlaneDomainOperation[];
  readonly stairClippingOperations: WallPlaneDomainOperation[];
  readonly wallInfo: WallPlaneSubdomainInfo | null;

  // index in the wall plane descriptor
  readonly arrayIndexU: number;
  readonly arrayIndexV: number;

  // containment ids relative to the building graph
  readonly topologyIds: string[];
  readonly faceNodeIds: string[];
  readonly faceEdgeIds: number[];
};

export type WallPlaneDescriptor = {
  // UV domain in the cannonical building graph plane
  readonly subdomainAABB: AABB2;

  // W domain in the cannonical building graph plane
  readonly subdomainMinW: number;
  readonly subdomainMaxW: number;

  readonly subdomains: WallPlaneSubdomain[][];
  readonly subdomainIndex: RBush<WallPlaneSubdomain>;
};

export interface WallPlane extends DynamicBuildingEntityBase<"WALL_PLANE"> {
  /** Deterministic and always equal to `planeHash`. */
  readonly id: string;
  readonly planeHash: string;
  readonly descriptor: WallPlaneDescriptor;
  readonly operations: WallPlaneDomainOperation[];
}

export interface WallPlaneSubdomainMiterEdge {
  readonly wallPlaneHash: string;
  /**
   * @TODO - swap for miterHash and index in the wall plane descriptor
   */
  readonly subdomain: WallPlaneSubdomain;

  readonly edge: PolyLine2;
  readonly plane: Plane;

  readonly bottomOffset: number;
  readonly topOffset: number;

  readonly leftCoreOffset: number;
  readonly leftCladdingOffset: number;

  readonly rightCoreOffset: number;
  readonly rightCladdingOffset: number;
}

export interface WallPlaneSubdomainMiterMass {
  readonly miterEdge: WallPlaneSubdomainMiterEdge;
  readonly miterPlane: Plane;

  // datums relative to miter plane
  readonly bottom: number;
  readonly top: number;

  // miter edges relative to miter plane
  readonly leftCladdingEdge?: PolyLine2;
  readonly leftCoreEdge?: PolyLine2;

  readonly rightCoreEdge?: PolyLine2;
  readonly rightCladdingEdge?: PolyLine2;

  /**
   * @TODO - WALLS V2 - for now we just store whether the miter edge has a cap
   * or not. but really this should populate more information about the start and
   * end miters
   */
  readonly startCap: boolean;
  readonly endCap: boolean;
}

export interface WallPlaneMassGrid {
  readonly grid: (WallPlaneSubdomainMiterMass | undefined)[][];
}

export type WallPlaneMasses = {
  readonly miters: Record<string, WallPlaneSubdomainMiterEdge[]>;
  /** TEMP OUTPUT */
  readonly masses: WallPlaneSubdomainMiterMass[];
  /** Per wall-plane-hash grids with U/V boundary values for adjacency lookups. */
  readonly massGrids: Record<string, WallPlaneMassGrid>;
};

/** Opening-derived SET_WALL_CUT domain operations keyed by wall plane hash. */
export type WallPlaneOpeningDomainOperationsByPlaneHash = Record<string, WallPlaneDomainOperation[]>;
